
import React, { useCallback, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { format } from 'date-fns';
import { CheckCircle2, Download, Calendar, ArrowUpRight, X, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { downloadICSFile } from '../utils/icsGenerator';
import { DEFAULT_BOOKING_SETTINGS } from '@/lib/constants/booking-defaults';
import { cn } from '@/lib/utils';
import { GradientBars } from '@/components/ui/bg-bars';

interface BookingSuccessScreenProps {
  selectedDate?: Date;
  selectedTime?: string;
  meetingLink: any;
  formData: any;
  debugSettings: any;
  fontClass: string;
  bookingId?: string; // For cancel/reschedule functionality
}

export const BookingSuccessScreen: React.FC<BookingSuccessScreenProps> = ({
  selectedDate,
  selectedTime,
  meetingLink,
  formData,
  debugSettings,
  fontClass,
  bookingId
}) => {
  // Trigger confetti animation when component mounts
  useEffect(() => {
    const triggerConfetti = () => {
      const count = 200;
      const defaults = {
        origin: { y: 0.7 }
      };

      function fire(particleRatio: number, opts: any) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio)
        });
      }

      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      });

      fire(0.2, {
        spread: 60,
      });

      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8
      });

      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2
      });

      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      });
    };

    const timeoutId = setTimeout(triggerConfetti, 300);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleDownloadICS = useCallback(() => {
    if (!selectedDate || !selectedTime) return;
    
    const startDateTime = new Date(selectedDate);
    const [hours, minutes] = selectedTime.split(':').map(Number);
    startDateTime.setHours(hours, minutes, 0, 0);
    
    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + debugSettings.duration);

    const businessName = meetingLink.profiles?.business_name || DEFAULT_BOOKING_SETTINGS.BUSINESS_NAME;
    
    downloadICSFile({
      title: meetingLink.name,
      description: `Meeting with ${businessName}${meetingLink.description ? `\n\n${meetingLink.description}` : ''}`,
      startDate: startDateTime,
      endDate: endDateTime,
      organizer: {
        name: businessName,
        email: 'noreply@chronocal.io'
      },
      attendee: {
        name: formData.name,
        email: formData.email
      }
    }, `${meetingLink.name.replace(/[^a-zA-Z0-9]/g, '_')}.ics`);
  }, [selectedDate, selectedTime, debugSettings.duration, meetingLink, formData]);

  const handleManageMeeting = useCallback(() => {
    if (!bookingId) return;
    
    // Navigate to the unified booking management page
    const manageUrl = `/booking/${bookingId}/manage`;
    window.location.href = manageUrl;
  }, [bookingId]);

  return (
    <div className={cn("min-h-screen flex flex-col items-center justify-center relative", fontClass)}>
      <GradientBars bars={25} colors={['#10b989CC', 'transparent']} />
      <Card className="max-w-2xl mx-4 z-10 border-none shadow-xl">
        <CardContent className="p-8 text-center relative z-10" data-testid="confirmation-message">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-2 text-emerald-500">Booking Confirmed!</h2>
          <p className="text-zinc-900 mb-4">
            {selectedDate && selectedTime ? (
              <>
                Your meeting has been scheduled for <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')} at {format(new Date(`2000-01-01T${selectedTime}`), 'h:mm a')}</strong>.
              </>
            ) : (
              'Your meeting has been successfully scheduled.'
            )}
          </p>
          <p className="text-md text-gray-900 mb-6">
            You'll receive a calendar invitation shortly.
          </p>

          <div className="flex flex-col items-center gap-3">
            {/* Primary Action - Download ICS */}
            <Button
              onClick={handleDownloadICS}
              variant="outline"
              className="w-full sm:w-auto min-w-[240px]"
              disabled={!selectedDate || !selectedTime}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Calendar File (.ics)
            </Button>
            
              {/* Secondary Actions - Manage Meeting */}
              {bookingId && (
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  <Button
                    onClick={handleManageMeeting}
                    variant="ghost"
                    className="w-full sm:w-auto min-w-[180px] text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reschedule or Cancel
                  </Button>
                </div>
              )}
          </div>

          <hr className="h-px mt-8 mb-4 bg-gray-100 border-0 dark:bg-gray-700" />

          <div className="space-y-2">
            <a href={`http://chronocal.io?utm_source=booking_page&utm_medium=footer&utm_campaign=meeting_links&utm_content=${meetingLink.id}`} target="_blank" rel="noopener noreferrer" className="font-bold inline-flex items-center">
              <img
                src="/assets/icons/branding/chronocal-logo.svg"
                alt="ChronoCal Logo"
                height={18}
                style={{ maxHeight: 18, margin: '10px auto', verticalAlign: 'middle', display: 'flex-inline' }}
              />
            </a>
            <p className="text-gray-600 text-sm">
              Unlike traditional schedulers, ChronoCal understands your natural energy rhythms and helps you optimize your day. <a href={`http://chronocal.io?utm_source=booking_page&utm_medium=footer&utm_campaign=meeting_links&utm_content=${meetingLink.id}`} target="_blank" rel="noopener noreferrer" className="font-bold inline-flex items-center gap-1">Get your own booking page <ArrowUpRight className="w-4 h-4 mt-0.5" aria-label="Opens in new tab" /></a>
            </p>
            <div>

            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
